import * as React from 'react';
import {CenteredEngagementCard} from 'epic-ue-newsletter';
import {ButtonProps, NewsletterData} from 'epic-ue-shared';
import {AnimatedSection, Page, SectionWrapper} from 'epic-ue-components';
import {getHelmetForMetaTags} from '../../utils/epicGamesUtils';
import {
    LicensingPage,
    TitleContainer,
    Title,
    SectionContainer,
    HalfSectionContainer,
    Divider,
    Section1,
    Section2,
    BottomLinksBlock,
    Text,
    PrimaryButton,
    TextButton,
    IncludedOption,
    InfoBlock,
    UnderlinedText
} from './Licensing.styles';
import {useLoading, useNotFound} from '../../utils/loadingUtils';
import Error404 from '../error/Error404';
import LoadIndicator from '../../components/common/loading/LoadIndicator';
import {PageData} from '@epic-mw/types';

import check from '../../../../assets/icons/check.svg';
import close from '../../../../assets/icons/close.svg';

export interface IncludedLicensingOption {
    title: string;
    presented: boolean;
}

export interface LicensingPageData {
    title: string;
    desc: string;
    subtitle: string;
    subdesc: string;
    packageTitle: string;
    packageDescription?: string;
    logo?: string; // Online Services logo
    logoAlt?: string;
    licensingFor: {
        title: string;
        options: IncludedLicensingOption[];
    };
    included: {
        title: string;
        options: IncludedLicensingOption[];
    };
    bottomBlock: {
        buttons: {
            label: string;
            url: string;
        }[];
    };
    newsletter: NewsletterData;
    succeed: {
        title: string;
        desc: string;
        buttons: ButtonProps[];
    };
}

interface LicensingContainerProps {
    pageData: LicensingPageData;
}

const LicensingContainer: React.FC<LicensingContainerProps> = ({pageData}) => {
    const isLoading = useLoading(pageData as PageData);
    const notFound = useNotFound(pageData as PageData);

    if (isLoading) {
        return <LoadIndicator fill />;
    }

    if (notFound) {
        return <Error404 />;
    }

    const safePageData = pageData || {};

    const {
        title,
        desc,
        subtitle,
        subdesc,
        packageTitle,
        packageDescription,
        logo,
        logoAlt = 'Online Services logo',
        included,
        licensingFor,
        bottomBlock,
        succeed,
        newsletter
    } = safePageData;
    const {buttons = []} = bottomBlock || {};
    const {title: forTitle, options: forOptions = []} = licensingFor || {};
    const {title: includedTitle, options: includedOptions = []} = included || {};
    const {title: succeedTitle, desc: succeedDesc, buttons: succeedButtons} = succeed || {};

    return (
        <Page>
            <LicensingPage>
                {getHelmetForMetaTags(pageData)}
                <TitleContainer>
                    {title && <Title>{title}</Title>}
                    {desc && <p className="desc">{desc}</p>}
                </TitleContainer>
                <SectionContainer>
                    <HalfSectionContainer>
                        <Section1>
                            {logo && <img src={logo} alt={logoAlt} loading="lazy" />}
                            <Title>{subtitle}</Title>
                            {subdesc && <p className="desc">{subdesc}</p>}
                        </Section1>
                    </HalfSectionContainer>
                    <Divider />
                    <HalfSectionContainer>
                        <Section2>
                            <InfoBlock>
                                <Text fontSize="18px" lineHeight="25px" letterSpacing="-0.2px" uppercase bold>
                                    {packageTitle}
                                </Text>
                                <Text letterSpacing="0.2px" dangerouslySetInnerHTML={{__html: packageDescription || ''}} />
                            </InfoBlock>
                            <div>
                                <Text fontSize="12px" lineHeight="15px" letterSpacing="0.5px" uppercase bold>
                                    {forTitle}:
                                </Text>
                                {forOptions.map(({title, presented}, i) => (
                                    <IncludedOption key={`${i}_${title}`}>
                                        <img src={presented ? check : close} alt="Included option mark" loading="lazy" />
                                        <Text letterSpacing="0.2px">{title}</Text>
                                    </IncludedOption>
                                ))}
                            </div>
                            <div>
                                <Text fontSize="12px" lineHeight="15px" letterSpacing="0.5px" uppercase bold>
                                    {includedTitle}:
                                </Text>
                                {includedOptions.map(({title, presented}, i) => (
                                    <IncludedOption key={`${i}_${title}`}>
                                        <img src={presented ? check : close} alt="Included option mark" loading="lazy" />
                                        <Text letterSpacing="0.2px">{title}</Text>
                                    </IncludedOption>
                                ))}
                            </div>
                            <BottomLinksBlock>
                                {buttons[0] && (
                                    <PrimaryButton as="a" href={buttons[0].url}>
                                        <Text fontSize="12px" lineHeight="15px" letterSpacing="0.5px" uppercase bold>
                                            {buttons[0].label}
                                        </Text>
                                    </PrimaryButton>
                                )}
                                {buttons[1] && buttons[2] && (
                                    <div>
                                        <TextButton as="a" href={buttons[1].url}>
                                            <UnderlinedText fontSize="12px" lineHeight="15px" letterSpacing="0.5px" uppercase bold>
                                                {buttons[1].label}
                                            </UnderlinedText>
                                        </TextButton>
                                        <Text>|</Text>
                                        <TextButton as="a" href={buttons[2].url}>
                                            <UnderlinedText fontSize="12px" lineHeight="15px" letterSpacing="0.5px" uppercase bold>
                                                {buttons[2].label}
                                            </UnderlinedText>
                                        </TextButton>
                                    </div>
                                )}
                            </BottomLinksBlock>
                        </Section2>
                    </HalfSectionContainer>
                </SectionContainer>
                <SectionWrapper className="type-branding">
                    <AnimatedSection title={succeedTitle} copy={succeedDesc} buttons={succeedButtons} />
                </SectionWrapper>
                <SectionWrapper>
                    <CenteredEngagementCard newsletterData={newsletter} disableIndustrySelect marketoLeadParams={{eOSSourceOptIn: true}} epicOptIn />
                </SectionWrapper>
            </LicensingPage>
        </Page>
    );
};

export default LicensingContainer;
